import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from './dialog';
import { Button } from './button';
import { Label } from './label';
import { Input } from './input';
import { ScrollArea } from './scroll-area';
import { Search, Building, LogOut } from 'lucide-react';
import oneSpaceApi from '../../lib/onespace-api';
import storage from '../../lib/storage';
import { useAuth } from '../../contexts/AuthContext';

interface Organization {
  id: string;
  name: string;
  is_current: boolean;
  logo_url?: string;
}

interface OrganizationSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OrganizationSelectorModal: React.FC<OrganizationSelectorModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { logout } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizations = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch organizations from API
      const response = await oneSpaceApi.get<Organization[]>('/api/organizations');
      setOrganizations(response.data);
    } catch (err) {
      console.error("Error fetching organizations:", err);
      setError("Er is een fout opgetreden bij het ophalen van de organisaties.");
      
      // Set demo data for development/testing
      setOrganizations([
        { id: '1', name: 'Mijn Huidige Organisatie', is_current: true },
        { id: '2', name: 'Tweede Organisatie BV', is_current: false },
        { id: '3', name: 'Derde Bedrijf', is_current: false },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchOrganizations();
    }
  }, [isOpen]);

  const switchToOrganization = async (organizationId: string) => {
    setIsLoading(true);
    try {
      // Call API to switch organization
      await oneSpaceApi.post(`/api/organizations/${organizationId}/switch`);
      
      // Reload the page to apply the new organization context
      window.location.reload();
    } catch (err) {
      console.error("Error switching organization:", err);
      setError("Er is een fout opgetreden bij het wisselen van organisatie.");
      setIsLoading(false);
    }
  };

  const filteredOrganizations = organizations.filter(org => 
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Organisaties</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Search box */}
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Zoek organisatie..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {/* Organizations list */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center py-4 text-destructive">{error}</div>
          ) : filteredOrganizations.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">Geen organisaties gevonden</div>
          ) : (
            <ScrollArea className="h-[300px] pr-3">
              <div className="space-y-2">
                {filteredOrganizations.map((org) => (
                  <div 
                    key={org.id}
                    className={`
                      p-3 rounded-md flex items-center justify-between cursor-pointer
                      ${org.is_current 
                        ? 'bg-primary/10 border border-primary/50' 
                        : 'hover:bg-accent border border-border'}
                    `}
                    onClick={() => !org.is_current && switchToOrganization(org.id)}
                  >
                    <div className="flex items-center gap-3">
                      {org.logo_url ? (
                        <img 
                          src={org.logo_url} 
                          alt={org.name} 
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <Building className="w-5 h-5 text-muted-foreground" />
                      )}
                      <div>
                        <p className="font-medium">{org.name}</p>
                        {org.is_current && (
                          <p className="text-xs text-muted-foreground">Huidige organisatie</p>
                        )}
                      </div>
                    </div>
                    {!org.is_current && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="opacity-50 hover:opacity-100"
                      >
                        Wisselen
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
          
          {/* Create new organization option */}
          <div
            className="p-3 rounded-md flex items-center justify-between cursor-pointer border border-dashed border-border hover:bg-accent/50"
            onClick={() => {
              // This would typically open a form to create a new organization
              // For now, just close the modal
              onClose();
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xl font-medium">+</span>
              </div>
              <p className="font-medium">Nieuwe organisatie aanmaken</p>
            </div>
          </div>
        </div>

        {/* Footer with logout option */}
        <DialogFooter className="flex justify-between items-center sm:justify-between">
          <Button 
            variant="outline" 
            size="sm"
            className="gap-2"
            onClick={() => {
              logout();
              onClose();
            }}
          >
            <LogOut className="h-4 w-4" />
            Uitloggen
          </Button>
          <DialogClose asChild>
            <Button variant="secondary" size="sm">Sluiten</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 